import { forwardRef, useState, useRef, useEffect, useCallback } from 'react'
import { Box, Flex, Input } from '@chakra-ui/react'
import { DayPicker } from 'react-day-picker'
import { es } from 'react-day-picker/locale'
import 'react-day-picker/style.css'
import { useFormColors } from '../../forms/FormThemeContext'
import { withFormLabel } from '../../hocs/withFormLabel'
import { withFormValidation } from '../../hocs/withFormValidation'
import { useResolveText } from '../../i18n'

interface BaseDatePickerProps {
  value?: string
  onChange?: (date: string) => void
  onBlur?: () => void
  placeholder?: string
  placeholderTid?: string
  disabled?: boolean
  minDate?: string
  maxDate?: string
  name?: string
}

function formatDisplay(isoDate: string): string {
  if (!isoDate) return ''
  const [y, m, d] = isoDate.split('-')
  if (!y || !m || !d) return ''
  return `${d}/${m}/${y}`
}

function toDate(isoDate: string): Date | undefined {
  if (!isoDate) return undefined
  const d = new Date(isoDate + 'T00:00:00')
  return isNaN(d.getTime()) ? undefined : d
}

function toIso(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const BaseDatePicker = forwardRef<HTMLInputElement, BaseDatePickerProps>(({ value = '', onChange, onBlur, placeholder, placeholderTid, disabled, minDate, maxDate }, ref) => {
  const c = useFormColors()
  const resolve = useResolveText()
  const resolvedPlaceholder = resolve(placeholderTid, placeholder) ?? 'Seleccionar fecha'
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const selected = toDate(value)

  const computeDropUp = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      return spaceBelow < 340 && rect.top > 340
    }
    return false
  }, [])

  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  const handleOpen = useCallback(() => {
    if (disabled) return
    computeDropUp()
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const up = computeDropUp()
      setDropdownPos({
        top: up ? rect.top - 340 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 300),
      })
    }
    setOpen((prev) => !prev)
  }, [disabled, computeDropUp])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      setOpen(false)
      onBlur?.()
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onBlur])

  return (
    <Box ref={containerRef} position="relative">
      <Box position="relative">
        <Input
          ref={ref}
          readOnly
          value={formatDisplay(value)}
          placeholder={resolvedPlaceholder}
          onClick={handleOpen}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          disabled={disabled}
          pr={value ? '4.5rem' : '2.5rem'}
        />
        <Flex position="absolute" right="0.5rem" top="50%" transform="translateY(-50%)" alignItems="center" gap={1}>
          {value && !disabled && (
            <Box
              p={1} borderRadius="sm" color={c.textDimmed}
              cursor="pointer" _hover={{ color: c.text, bg: c.bgHover }}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onChange?.('') }}
              role="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Box>
          )}
          <Box pointerEvents="none" color={c.textDimmed}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </Box>
        </Flex>
      </Box>
      {open && (
        <Box
          ref={dropdownRef}
          position="fixed"
          top={`${dropdownPos.top}px`}
          left={`${dropdownPos.left}px`}
          zIndex={9999} bg={c.bgCard} borderRadius="sm" boxShadow="lg" p={3}
          border="1px solid" borderColor={c.border}
        >
          <style>{`
            .minetrace-datepicker .rdp-root {
              --rdp-accent-color: ${c.accent};
              --rdp-accent-background: ${c.accent}20;
              --rdp-day-height: 36px; --rdp-day-width: 36px;
              font-family: inherit; font-size: 0.875rem;
            }
            .minetrace-datepicker .rdp-day button { border-radius: 0.5rem; color: ${c.text}; }
            .minetrace-datepicker .rdp-day button:hover { background: ${c.bgInput}; }
            .minetrace-datepicker .rdp-selected .rdp-day_button { background: ${c.accent} !important; color: white !important; }
            .minetrace-datepicker .rdp-caption_label, .minetrace-datepicker .rdp-month_caption { color: ${c.text}; font-weight: 600; }
            .minetrace-datepicker .rdp-weekday { color: ${c.textMuted}; font-size: 0.75rem; }
            .minetrace-datepicker .rdp-button_previous, .minetrace-datepicker .rdp-button_next { color: ${c.textSecondary}; }
            .minetrace-datepicker .rdp-outside .rdp-day_button { color: ${c.textMuted}; opacity: 0.5; }
            .minetrace-datepicker .rdp-today .rdp-day_button { font-weight: 700; color: ${c.accent}; }
            .minetrace-datepicker .rdp-dropdowns { display: flex; gap: 0.5rem; align-items: center; }
            .minetrace-datepicker .rdp-dropdown_root { position: relative; }
            .minetrace-datepicker .rdp-dropdown {
              background: ${c.bgInput}; color: ${c.text}; border: 1px solid ${c.border};
              border-radius: 0.375rem; padding: 0.25rem 0.5rem; font-size: 0.875rem;
              font-weight: 600; cursor: pointer; appearance: none;
            }
            .minetrace-datepicker .rdp-dropdown:focus { outline: 2px solid ${c.accent}; outline-offset: 1px; }
          `}</style>
          <div className="minetrace-datepicker">
            <DayPicker
              mode="single"
              captionLayout="dropdown"
              startMonth={minDate ? new Date(minDate + 'T00:00:00') : new Date(1940, 0)}
              endMonth={maxDate ? new Date(maxDate + 'T00:00:00') : new Date(new Date().getFullYear() + 10, 11)}
              selected={selected}
              onSelect={(date) => { onChange?.(date ? toIso(date) : ''); setOpen(false) }}
              defaultMonth={selected}
              locale={es}
              disabled={[
                ...(minDate ? [{ before: new Date(minDate + 'T00:00:00') }] : []),
                ...(maxDate ? [{ after: new Date(maxDate + 'T00:00:00') }] : []),
              ]}
            />
          </div>
        </Box>
      )}
    </Box>
  )
})
BaseDatePicker.displayName = 'BaseDatePicker'

// Standalone (controlado por value/onChange, sin react-hook-form) para modales y filtros.
export const DatePicker = BaseDatePicker
export const LabeledDatePicker = withFormLabel(BaseDatePicker)
export const TrackedDatePicker = withFormValidation(LabeledDatePicker)
